import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import { useParams, useNavigate } from 'react-router';
import { GetJoinOrganizationDetailDocument, GetJoinOrganizationDetailQuery } from 'graphql/queries.generated';
import { ApplyForMembershipDocument } from 'graphql/mutations.generated';
import { useUser } from 'contexts/UserContext';
import Image from 'components/Image';
import { ImageOwnerType, ImageSize } from 'app-constants';
import styles from 'pages/JoinGroup.module.css';
import globalStyles from 'Global.module.css';

const JoinGroup: React.FC = () => {
    const { organizationId } = useParams<{ organizationId: string }>();
    const navigate = useNavigate();
    const { user } = useUser();
  
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { loading, error: queryError, data } = useQuery<GetJoinOrganizationDetailQuery>(
    GetJoinOrganizationDetailDocument,
    {
      variables: {
        organizationId: organizationId || ''
      },
      skip: !organizationId
    }
  );

  const [applyForMembership] = useMutation(ApplyForMembershipDocument);

  if (!organizationId) {
    return (
      <div className={styles.joinGroup}>
        <p className={styles.error}>No organization ID provided</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={styles.joinGroup}>
        <p className={styles.loading}>Loading...</p>
      </div>
    );
  }

  if (queryError) {
    return (
      <div className={styles.joinGroup}>
        <p className={styles.error}>Error loading organization: {queryError.message}</p>
      </div>
    );
  }

  const organization = data?.organization;

  if (!organization) {
    return (
      <div className={styles.joinGroup}>
        <p className={styles.error}>Organization not found</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className={styles.joinGroup}>
        <p className={styles.error}>You must be logged in to join an organization</p>
      </div>
    );
  }

  const handleAnswerChange = (questionId: string, value: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const answerInputs = organization.membershipQuestions
        .filter(q => q !== null)
        .map(question => ({
          organizationMembershipQuestionId: question!.organizationMembershipQuestionId,
          answer: answers[question!.organizationMembershipQuestionId] || ''
        }));

      await applyForMembership({
        variables: {
          organizationId,
          personId: user.personId,
          answers: answerInputs
        }
      });

      navigate(`/groups/${organizationId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while applying for membership');
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.joinGroup}>
      <div className={styles.banner}>
        <Image 
          imageOwnerType={ImageOwnerType.Organization} 
          imageSize={ImageSize.Banner} 
          imageId={organization.imageId} 
        />
      </div>
      
      <div className={styles.content}>
        <h1>Join {organization.name}</h1>
        
        {organization.membershipQuestions.length > 0 ? (
          <form onSubmit={handleSubmit} className={styles.form}>
            <p className={styles.intro}>
              Please answer the following questions to apply for membership:
            </p>
            
            {organization.membershipQuestions.map((question, index) => {
              if (!question) return null;
              
              return (
                <div key={question.organizationMembershipQuestionId} className={styles.questionContainer}>
                  <label htmlFor={`question-${index}`} className={styles.questionLabel}>
                    {question.question}
                  </label>
                  <textarea
                    id={`question-${index}`}
                    className={styles.answerTextarea}
                    value={answers[question.organizationMembershipQuestionId] || ''}
                    onChange={(e) => handleAnswerChange(question.organizationMembershipQuestionId, e.target.value)}
                    rows={4}
                    placeholder="Type your answer here..."
                  />
                </div>
              );
            })}

            {error && (
              <div className={styles.error}>{error}</div>
            )}

            <div className={styles.buttonContainer}>
              <button 
                type="submit" 
                className={globalStyles.submitButton}
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Submit Application'}
              </button>
              <button 
                type="button" 
                className={globalStyles.cancelButton}
                onClick={() => navigate(`/groups/${organizationId}`)}
                disabled={submitting}
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <div className={styles.noQuestions}>
            <p>This organization does not require any additional information to join.</p>
            <button 
              className={globalStyles.submitButton}
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? 'Joining...' : 'Join Now'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default JoinGroup;

